import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import usePlayer from '@fec/frontend-aron/foundation/hooks/usePlayer';
import usePlayerIsBlocked from '@fec/frontend-aron/foundation/hooks/usePlayerIsBlocked';
import BasicCaption from '@fec/frontend-aron/elements/media/BasicCaption/BasicCaption';
import MediaBlockingNotice from '@fec/frontend-aron/elements/media/MediaBlockingNotice/MediaBlockingNotice';
import Image from '@fec/frontend/elements/media/image/Image';
import PlayIcon from '@fec/frontend/elements/icons/play-icon/PlayIcon';
import MediaTimeIndicator from '@fec/frontend/elements/media/media-time-indicator/MediaTimeIndicator';
import usePlayerA11yHelper from '@fec/frontend-aron/foundation/hooks/usePlayerA11yHelper';

import './BasicVideo.scss';

const BasicVideo = ({ asset, isTopMediaElement }) => {
  const { urn, frontendId, duration = 0, image = null } = asset;
  const [isInitialized, setIsInitialized] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const { isBlocked, reason } = usePlayerIsBlocked(asset);
  const playerContainerRef = useRef(null);
  const { activePlayerLabel, a11yAttributes } = usePlayerA11yHelper(
    asset,
    isBlocked,
  );
  const { playerIsActive, dispatchMediaClickedEvent } = usePlayer({
    urn,
    frontendId,
    containerRef: playerContainerRef,
    initialize: isInitialized,
    playerA11yLabel: activePlayerLabel,
  });

  useEffect(() => {
    setIsActive(playerIsActive);
  }, [playerIsActive]);

  const initializePlayer = () => {
    if (isInitialized || isBlocked) {
      return;
    }
    setIsInitialized(true);
    dispatchMediaClickedEvent();
  };

  const initializePlayerByKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      initializePlayer();
    }
  };

  const handleCaptionClick = () => {
    dispatchMediaClickedEvent();
  };

  return (
    <div
      className={classNames('basic-video', {
        'basic-video--active': isActive,
        'basic-video--blocked': isBlocked,
        'js-player-active': isActive, // inform non-react components (i.e. TME container) about player state
      })}
      {...(!isInitialized && a11yAttributes)}
      onClick={() => initializePlayer()}
      onKeyDown={(e) => initializePlayerByKey(e)}>
      {!isInitialized && (
        <>
          <div className="basic-video__image-container" aria-hidden="true">
            <Image image={image} noBackground={isTopMediaElement} />
          </div>
          {!isBlocked && (
            <>
              <div className="basic-video__play-button" aria-hidden="true">
                <PlayIcon type="video" />
              </div>
              <div className="basic-video__duration" aria-hidden="true">
                <MediaTimeIndicator duration={duration} />
              </div>
            </>
          )}
        </>
      )}

      <div
        className="basic-video__player-container"
        ref={playerContainerRef}></div>

      {isBlocked && (
        <div className="basic-video__notice">
          <MediaBlockingNotice asset={asset} reason={reason} />
        </div>
      )}

      <div
        className="basic-video__caption js-basic-video-caption"
        aria-hidden="true"
        onClick={() => handleCaptionClick()}>
        <BasicCaption asset={asset} isActive={isActive} />
      </div>
    </div>
  );
};

BasicVideo.propTypes = {
  asset: PropTypes.object,
  isTopMediaElement: PropTypes.bool,
};

export default BasicVideo;
